import { useState } from 'react';
import type { Dayjs } from 'dayjs';
import { Alert, Button, DatePicker, Form, Image, Input, Modal, Space, Upload, notification } from 'antd';
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import CustomButton from 'components/custom-button';
import { refreshPendingCounts } from 'layouts/pending-counts';
import { imageUrl, parseKcInput } from 'pages/star-shared/utils';
import type {
  StudioCandidate,
  StudioMembersResponse,
  StudioRequestKind,
  StudioRequestRow,
} from '../contract';
import { CreateStudioRequestBody, createStudioRequest, uploadStudioImage } from '../api';
import { mapStudioError } from '../studio-error';
import {
  KC_DAY_HAS_FILE_HINT,
  REASON_MAX,
  REASON_MIN,
  REQUEST_SENT_NOTE,
  STUDIO_KC_REQUEST_MAX,
  STUDIO_KC_REQUEST_WINDOW_DAYS,
} from '../labels';
import { addDays, utcToday } from '../format';
import MemberCell from './member-cell';
import MemberPicker from './member-picker';
import TeamChannelModal, { TeamChannelTeam } from './team-channel-modal';

/** Người cố định của đề nghị gỡ / nhập KC (lấy từ dòng StudioMemberRow). */
export interface RequestModalMember {
  key: string;
  userId: number | null;
  uid: string | null;
  nickName?: string | null;
  username?: string | null;
  avatar?: string | null;
}

export interface RequestModalProps {
  open: boolean;
  studioId: number;
  requestKind: StudioRequestKind;
  /** MEMBER_REMOVE, KC_MANUAL: người cố định theo dòng. */
  member?: RequestModalMember | null;
  /** TEAM_CHANNEL: nhóm cố định (mở từ tab Nhóm nhảy); chuyển sang TeamChannelModal chế độ đề nghị. */
  team?: TeamChannelTeam | null;
  /** TEAM_CHANNEL: gắn / đổi (mặc định) hay bỏ kênh. */
  channelAction?: 'set' | 'clear';
  /** KC_MANUAL: độ phủ ngày của S10 để khoá các ngày đã có số từ file. */
  coverage?: StudioMembersResponse['coverage'];
  onClose: () => void;
  onDone?: (row: StudioRequestRow) => void;
}

interface FormValues {
  candidate?: StudioCandidate[];
  kcDate?: Dayjs;
  kc?: string;
  reason: string;
  evidenceUrl?: string;
}

const TITLE: Record<StudioRequestKind, string> = {
  MEMBER_ADD: 'Đề nghị thêm người',
  MEMBER_REMOVE: 'Đề nghị gỡ người',
  KC_MANUAL: 'Đề nghị nhập kim cương',
  TEAM_CHANNEL: 'Đề nghị gắn kênh',
};

const MAX_IMAGE_MB = 10;
/** Số KC hợp lệ: 150000, 150.000, 150,000, 150 000 (không dấu âm, không phần lẻ, không chữ). */
const KC_INPUT_PATTERN = /^(\d+|\d{1,3}(\.\d{3})+|\d{1,3}(,\d{3})+|\d{1,3}( \d{3})+)$/;

// ----------------------------------------------------------- ô minh chứng ---

interface EvidenceFieldProps {
  value?: string;
  onChange?: (value?: string) => void;
}

/**
 * Minh chứng CHỈ là ảnh tải lên từ modal (mục 8.7.5): giá trị là khoá 'hp/<uuid>' do POST /image/storages
 * trả; không có ô dán link.
 */
const EvidenceField = ({ value, onChange }: EvidenceFieldProps) => {
  const [uploading, setUploading] = useState(false);

  const beforeUpload = (file: File) => {
    if (!/^image\/(png|jpe?g)$/i.test(file.type)) {
      notification.error({ message: 'Chỉ nhận ảnh PNG hoặc JPG.' });
      return Upload.LIST_IGNORE;
    }
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      notification.error({ message: `Ảnh không được lớn hơn ${MAX_IMAGE_MB} MB.` });
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  return (
    <Space align="start" wrap>
      {value && <Image src={imageUrl(value)} width={96} height={96} style={{ objectFit: 'cover' }} />}
      <Space direction="vertical">
        <Upload
          accept="image/png,image/jpeg"
          showUploadList={false}
          beforeUpload={beforeUpload}
          customRequest={async ({ file, onSuccess, onError }) => {
            setUploading(true);
            try {
              const key = await uploadStudioImage(file as File);
              onChange?.(key);
              onSuccess?.(key);
            } catch (err) {
              notification.error({ message: mapStudioError(err) });
              onError?.(err as Error);
            } finally {
              setUploading(false);
            }
          }}
        >
          <Button icon={<UploadOutlined />} loading={uploading}>
            {value ? 'Đổi ảnh' : 'Tải ảnh lên'}
          </Button>
        </Upload>
        {value && (
          <Button icon={<DeleteOutlined />} onClick={() => onChange?.(undefined)} disabled={uploading}>
            Xoá ảnh
          </Button>
        )}
      </Space>
    </Space>
  );
};

// ------------------------------------------------------------------ modal ---

const reasonRules = [
  {
    validator: (_: unknown, value?: string) =>
      (value || '').trim().length >= REASON_MIN
        ? Promise.resolve()
        : Promise.reject(new Error(`Lý do cần ít nhất ${REASON_MIN} ký tự.`)),
  },
];

const MemberRequestModal = ({
  open,
  studioId,
  requestKind,
  member,
  coverage,
  onClose,
  onDone,
}: RequestModalProps) => {
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState(false);
  const evidence = Form.useWatch('evidenceUrl', form);
  const isKc = requestKind === 'KC_MANUAL';
  const needsMember = requestKind === 'MEMBER_REMOVE' || isKc;
  const kcBlocked = isKc && (!member?.userId || !member?.uid);

  const today = utcToday();
  const minDay = addDays(today, -STUDIO_KC_REQUEST_WINDOW_DAYS);
  // `coverage` của S10 chỉ có tháng UTC hiện tại và tháng trước; cửa sổ 35 ngày vào ngày 01–04 lùi sang tháng
  // thứ ba. Tháng cũ hơn mọi tháng trong `coverage` coi như đã có file cả tháng (khoá, cùng tooltip).
  const oldestCovered = coverage?.length
    ? coverage.reduce((min, c) => (c.month < min ? c.month : min), coverage[0].month)
    : null;
  const coveredOn = (day: string) => {
    const month = day.slice(0, 7);
    if (oldestCovered && month < oldestCovered) return true;
    const cov = coverage?.find((c) => c.month === month);
    return !!cov && Number(day.slice(8, 10)) <= cov.coveredThrough;
  };
  const disabledDate = (current: Dayjs) => {
    const day = current.format('YYYY-MM-DD');
    return day > today || day < minDay || coveredOn(day);
  };

  const handleClose = () => {
    if (saving) return;
    form.resetFields();
    onClose();
  };

  const buildBody = (values: FormValues): CreateStudioRequestBody | null => {
    const reason = values.reason.trim();
    const evidenceUrl = values.evidenceUrl || undefined;
    if (requestKind === 'MEMBER_ADD') {
      const c = values.candidate?.[0];
      if (!c) return null;
      return c.userId !== null && c.userId !== undefined
        ? { requestKind, reason, evidenceUrl, userId: c.userId }
        : { requestKind, reason, evidenceUrl, uid: c.uid || undefined };
    }
    if (!member) return null;
    if (requestKind === 'MEMBER_REMOVE') {
      return { requestKind, reason, evidenceUrl, key: member.key };
    }
    const kc = parseKcInput(values.kc);
    if (!member.userId || !values.kcDate || kc === null || !evidenceUrl) return null;
    return {
      requestKind: 'KC_MANUAL',
      reason,
      evidenceUrl,
      userId: member.userId,
      kcDate: values.kcDate.format('YYYY-MM-DD'),
      kc,
    };
  };

  const onFinish = async (values: FormValues) => {
    const body = buildBody(values);
    if (!body) {
      notification.error({ message: 'Thiếu thông tin của đề nghị.' });
      return;
    }
    setSaving(true);
    try {
      const row = await createStudioRequest(studioId, body);
      notification.success({ message: 'Đã gửi đề nghị tới Admin.' });
      refreshPendingCounts();
      form.resetFields();
      onDone?.(row);
      onClose();
    } catch (err) {
      notification.error({ message: mapStudioError(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={TITLE[requestKind]}
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnClose
      maskClosable={false}
      width={600}
    >
      <Alert type="info" showIcon message={REQUEST_SENT_NOTE} style={{ marginBottom: 16 }} />
      {needsMember && member && (
        <div style={{ marginBottom: 16 }}>
          <MemberCell
            nickName={member.nickName}
            username={member.username}
            uid={member.uid}
            avatar={member.avatar}
          />
        </div>
      )}
      {kcBlocked && (
        <Alert
          type="warning"
          showIcon
          message="Chỉ đề nghị nhập kim cương cho Creator có tài khoản app và có UID TikTok."
          style={{ marginBottom: 16 }}
        />
      )}
      <Form form={form} layout="vertical" className="custom-form" onFinish={onFinish}>
        {requestKind === 'MEMBER_ADD' && (
          <Form.Item
            label="Người được đề nghị"
            name="candidate"
            extra="Tìm theo tên, TikTok ID hoặc UID (ít nhất 2 ký tự). Chọn được cả Creator chưa có tài khoản app."
            rules={[{ required: true, type: 'array', min: 1, message: 'Chọn một người.' }]}
          >
            <MemberPicker studioId={studioId} />
          </Form.Item>
        )}
        {isKc && (
          <>
            <Form.Item
              label="Ngày"
              name="kcDate"
              extra={`Ngày theo giờ TikTok (UTC), trong ${STUDIO_KC_REQUEST_WINDOW_DAYS} ngày gần đây. Ngày đã có số từ file TikTok không chọn được.`}
              rules={[{ required: true, message: 'Chọn ngày.' }]}
            >
              <DatePicker
                format="DD/MM/YYYY"
                style={{ width: '100%' }}
                disabledDate={disabledDate}
                disabled={kcBlocked}
                cellRender={(current, info) => {
                  if (info.type !== 'date' || typeof current !== 'object') return info.originNode;
                  const day = (current as Dayjs).format('YYYY-MM-DD');
                  return coveredOn(day) ? (
                    <div title={KC_DAY_HAS_FILE_HINT}>{info.originNode}</div>
                  ) : (
                    info.originNode
                  );
                }}
              />
            </Form.Item>
            <Form.Item
              label="Số kim cương"
              name="kc"
              extra="Nhận 150000, 150.000 hoặc 150,000."
              rules={[
                {
                  validator: (_, value?: string) => {
                    const text = (value || '').trim();
                    if (!text) return Promise.reject(new Error('Nhập số kim cương.'));
                    // parseKcInput bỏ MỌI ký tự không phải số ('-150.000' → 150000, '1,5tr' → 15):
                    // chỉ nhận số nguyên, có thể tách nghìn bằng MỘT loại dấu '.', ',' hoặc khoảng trắng.
                    if (!KC_INPUT_PATTERN.test(text)) {
                      return Promise.reject(new Error('Chỉ nhập số nguyên, VD 150.000.'));
                    }
                    const kc = parseKcInput(text);
                    if (kc === null) return Promise.reject(new Error('Nhập số kim cương.'));
                    if (kc > STUDIO_KC_REQUEST_MAX) {
                      return Promise.reject(new Error('Số kim cương quá lớn.'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input inputMode="numeric" placeholder="VD: 150.000" disabled={kcBlocked} />
            </Form.Item>
          </>
        )}
        <Form.Item label="Lý do" name="reason" rules={reasonRules}>
          <Input.TextArea rows={3} maxLength={REASON_MAX} showCount placeholder="Nhập lý do" />
        </Form.Item>
        <Form.Item
          label={isKc ? 'Ảnh chụp Backstage' : 'Minh chứng (không bắt buộc)'}
          name="evidenceUrl"
          required={isKc}
          rules={
            isKc ? [{ required: true, message: 'Cần ảnh chụp Backstage làm minh chứng.' }] : []
          }
        >
          <EvidenceField />
        </Form.Item>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <CustomButton text="Huỷ" bgColor="primary-grey" onClick={handleClose} />
          <CustomButton
            text="Gửi đề nghị"
            bgColor="secondary-green"
            htmlType="submit"
            loading={saving}
            disabled={kcBlocked || (isKc && !evidence)}
          />
        </div>
      </Form>
    </Modal>
  );
};

/**
 * Quản lý Studio gửi đề nghị tới Admin (mục 8.7.5): Thêm người (ô chọn S26, chọn được Creator chưa có
 * tài khoản), Gỡ người, Nhập kim cương (ngày trong 35 ngày gần đây theo UTC, khoá ngày đã có số từ file,
 * ảnh chụp Backstage bắt buộc), Gắn kênh Nhóm nhảy (chuyển sang TeamChannelModal chế độ đề nghị).
 */
const RequestModal = (props: RequestModalProps) => {
  if (props.requestKind === 'TEAM_CHANNEL') {
    return (
      <TeamChannelModal
        open={props.open}
        studioId={props.studioId}
        team={props.team ?? null}
        mode="request"
        action={props.channelAction}
        onClose={props.onClose}
        onDone={(result) => result.request && props.onDone?.(result.request)}
      />
    );
  }
  return <MemberRequestModal {...props} />;
};

export default RequestModal;
